import React, { useEffect, useMemo } from 'react';
import { Row, Col, Typography, Select, Input, DatePicker, TimePicker } from 'antd';
import { PickupQuestion } from './PickupQuestion';
import { IActivityAgeBand, Question, QuestionCodesExclude, QuestionGroupTraveler, getActivityAgeBandBounds, isDateOfBirthQuestion, isTravelerAgeBandQuestion } from '@common-types';
import { setBookingAnswer, setTravelerAnswer } from '@share/store/slices';
import { useDispatch } from 'react-redux';

import { useIntl } from 'react-intl';
import moment from 'moment';
import { IGuestPayload } from '@share/common-types';
import { filterTransferModeQuestionOptions, getTransferModeResolvedOptions, getVisibleBookingQuestionTree, type BookingQuestionTreeNode, type QuestionMetaForOptions, AppDispatch } from '@share/utils';

const { Text } = Typography;
const { Option } = Select;

const TRANSFER_MODE_OPTION_CODES = new Set(['AIR', 'RAIL', 'SEA', 'OTHER']);

const TRANSFER_MODE_OPTION_DEFAULTS: Record<string, string> = {
    AIR: 'Airport',
    RAIL: 'Train station',
    SEA: 'Port',
    OTHER: 'Hotel / specific address',
};

function isTransferModeBookingQuestion(code: string | undefined): boolean {
    return code === 'TRANSFER_ARRIVAL_MODE' || code === 'TRANSFER_DEPARTURE_MODE';
}

/** Which transfer-mode subtree a pickup/dropoff question belongs to (from tree ancestry). */
type TransferPickupBranch = 'arrival' | 'departure' | null;

function nextTransferPickupBranch(
    parentQuestion: Question & { id?: string },
    inherited: TransferPickupBranch
): TransferPickupBranch {
    const code = (parentQuestion.code || parentQuestion.id || '').toUpperCase();
    if (code === 'TRANSFER_ARRIVAL_MODE') return 'arrival';
    if (code === 'TRANSFER_DEPARTURE_MODE') return 'departure';
    return inherited;
}

function formatTransferModeOptionLabel(
    intl: ReturnType<typeof useIntl>,
    questionCode: string | undefined,
    opt: { code: string; text: string }
): string {
    if (!isTransferModeBookingQuestion(questionCode)) {
        return opt.text;
    }
    const code = (opt.code || '').toUpperCase();
    if (!TRANSFER_MODE_OPTION_CODES.has(code)) {
        return opt.text;
    }
    const hint = intl.formatMessage({
        id: `checkout.transferMode.option.${code}`,
        defaultMessage: TRANSFER_MODE_OPTION_DEFAULTS[code] ?? opt.text,
    });
    return `${code} - ${hint}`;
}

interface CheckoutQuestionsProps {
    detailLabel: any;
    bookingAnswers: any;
    travelerAnswers: any;
    conditionalData: any;
    questionData: any;
}

const RenderDynamicInput: React.FC<{
    q: Question;
    answer: { value: any; unit?: string } | undefined;
    onChange: (val: any) => void;
    guestInfo?: IGuestPayload;
    activityDateIso?: string;
    ageBands?: IActivityAgeBand[] | null;
    /** Full product booking questions; used to filter transfer arrival/departure mode options. */
    productQuestions?: Question[];
    questionMetaList?: QuestionMetaForOptions[] | null;
}> = ({ q, answer, onChange, guestInfo, activityDateIso, ageBands, productQuestions, questionMetaList }) => {
    const intl = useIntl();
    const isDOB = isDateOfBirthQuestion(q);
    const isTransferMode = isTransferModeBookingQuestion(q.code);
    const selectOptions = isTransferMode
        ? getTransferModeResolvedOptions(q, productQuestions, questionMetaList)
        : q.options && q.options.length > 0
          ? filterTransferModeQuestionOptions(q.code, q.options as { code: string; text: string }[], productQuestions)
          : [];

    const disabledDate = (current: any) => {
        if (!isDOB) return false;

        if (current && current > moment().endOf('day')) {
            return true;
        }

        if (!guestInfo) return false;

        const ref = activityDateIso ? moment(activityDateIso, 'YYYY-MM-DD', true) : null;
        if (ref?.isValid() && guestInfo.type) {
            const bounds = getActivityAgeBandBounds(guestInfo.type, ageBands);
            if (bounds) {
                const age = ref.diff(current.startOf('day'), 'year');
                return !Number.isFinite(age) || age < bounds.startAge || age > bounds.endAge;
            }
        }

        const years = moment().diff(current, 'year');
        if (guestInfo.type === 'ADULT') {
            return years < 18;
        } else if (guestInfo.type === 'CHILD') {
            return years < 2 || years >= 18;
        } else if (guestInfo.type === 'INFANT') {
            return years >= 2;
        }
        return false;
    };

    const defaultPickerValue = isDOB && guestInfo?.age ? moment().subtract(guestInfo.age, 'year') : undefined;

    if (selectOptions.length > 0) {
        return (
            <Select
                value={answer?.value}
                onChange={onChange}
                style={{ width: '100%' }}
                placeholder={intl.formatMessage({ id: 'checkout.selectQuestion', defaultMessage: `Select ${q.text}` }, { question: q.text })}
            >
                {selectOptions.map((opt) => (
                    <Option key={opt.code} value={opt.code}>
                        {formatTransferModeOptionLabel(intl, q.code, opt)}
                    </Option>
                ))}
            </Select>
        );
    }

    switch (q.type) {
        case 'DATE':
            return (
                <DatePicker
                    onChange={(_: any, dateString: string | string[]) => onChange(dateString)}
                    style={{ width: '100%' }}
                    disabledDate={disabledDate}
                    defaultPickerValue={defaultPickerValue}
                />
            );
        case 'TIME':
            return <TimePicker onChange={(_: any, timeString: string | string[]) => onChange(timeString)} style={{ width: '100%' }} format="HH:mm" />;
        case 'NUMBER_AND_UNIT':
        case 'STRING':
        case 'LOCATION_REF_OR_FREE_TEXT':
        default:
            return (
                <Input
                    value={answer?.value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={q.text}
                />
            );
    }
};

export const GuestQuestions: React.FC<{
    idx: number;
    guest: IGuestPayload;
    detailLabel: any;
    travelerAnswers: any;
    activityDateIso?: string;
    ageBands?: IActivityAgeBand[] | null;
    questionMetaList?: QuestionMetaForOptions[] | null;
}> = ({ idx, guest, detailLabel, travelerAnswers, activityDateIso, ageBands, questionMetaList }) => {
    const dispatch = useDispatch<AppDispatch>();

    return (
        <Row gutter={[16, 16]} className="mt-2">
            {detailLabel.questions?.filter((q: any) => {
                const isGuestGroup = QuestionGroupTraveler.includes(q.group);
                const label = q.code || q.text?.toUpperCase() || '';
                const isDuplicate =
                    QuestionCodesExclude.includes(label) ||
                    isTravelerAgeBandQuestion(q);
                return isGuestGroup && !isDuplicate;
            }).map((q: any) => (
                <Col span={12} key={q.code}>
                    <div className="form-item text-start">
                        <Text className="label required">{q.text}</Text>
                        <Row gutter={8}>
                            <Col flex="auto">
                                <RenderDynamicInput
                                    q={q}
                                    productQuestions={detailLabel.questions as Question[] | undefined}
                                    questionMetaList={questionMetaList}
                                    guestInfo={guest}
                                    activityDateIso={activityDateIso}
                                    ageBands={ageBands}
                                    answer={travelerAnswers[idx]?.[q.code]}
                                    onChange={(val: any) => {
                                        dispatch(setTravelerAnswer({ index: idx, questionId: q.code, value: val, unit: travelerAnswers[idx]?.[q.code]?.unit }));
                                    }}
                                />
                            </Col>
                            {q.units && q.units.length > 0 && (
                                <Col>
                                    <Select
                                        value={travelerAnswers[idx]?.[q.code]?.unit || q.units[0]}
                                        onChange={(u: string) => dispatch(setTravelerAnswer({ index: idx, questionId: q.code, value: travelerAnswers[idx]?.[q.code]?.value, unit: u }))}
                                        style={{ width: 80 }}
                                    >
                                        {q.units.map((unit: string) => (
                                            <Option key={unit} value={unit}>
                                                {unit}
                                            </Option>
                                        ))}
                                    </Select>
                                </Col>
                            )}
                        </Row>
                    </div>
                </Col>
            ))}
        </Row>
    );
};

export const BookingQuestions: React.FC<CheckoutQuestionsProps> = ({
    detailLabel,
    bookingAnswers,
    conditionalData,
    questionData,
}) => {
    const dispatch = useDispatch<AppDispatch>();

    const productQuestions: Question[] | undefined = detailLabel.questions;

    useEffect(() => {
        if (!productQuestions?.length || !bookingAnswers) return;
        (['TRANSFER_ARRIVAL_MODE', 'TRANSFER_DEPARTURE_MODE'] as const).forEach((qcode) => {
            const q = productQuestions.find((x: any) => x.code === qcode);
            if (!q?.options?.length) return;
            const filtered = getTransferModeResolvedOptions(
                q as Question,
                productQuestions,
                questionData?.questions as QuestionMetaForOptions[] | undefined
            );
            const allowed = new Set(filtered.map((o) => String(o.code).toUpperCase()));
            const raw = bookingAnswers[qcode]?.value;
            if (raw === undefined || raw === null || raw === '') return;
            const v = String(raw).toUpperCase();
            if (!allowed.has(v)) {
                dispatch(
                    setBookingAnswer({
                        questionId: qcode,
                        value: '',
                        unit: bookingAnswers[qcode]?.unit,
                    })
                );
            }
        });
    }, [productQuestions, bookingAnswers, dispatch, questionData?.questions]);

    const bookingQuestionTree = useMemo(
        () => getVisibleBookingQuestionTree(detailLabel.questions, bookingAnswers, conditionalData),
        [detailLabel.questions, bookingAnswers, conditionalData]
    );

    const transferArrivalModeVisibleForCheckout = useMemo(() => {
        const walk = (nodes: BookingQuestionTreeNode[]): boolean => {
            for (const n of nodes) {
                if ((n.question as { code?: string; id?: string }).code === 'TRANSFER_ARRIVAL_MODE') {
                    return true;
                }
                if (walk(n.children)) {
                    return true;
                }
            }
            return false;
        };
        return walk(bookingQuestionTree);
    }, [bookingQuestionTree]);

    const transferDepartureModeVisibleForCheckout = useMemo(() => {
        const walk = (nodes: BookingQuestionTreeNode[]): boolean => {
            for (const n of nodes) {
                if ((n.question as { code?: string; id?: string }).code === 'TRANSFER_DEPARTURE_MODE') {
                    return true;
                }
                if (walk(n.children)) {
                    return true;
                }
            }
            return false;
        };
        return walk(bookingQuestionTree);
    }, [bookingQuestionTree]);

    const pickupTransferRulesApply =
        transferArrivalModeVisibleForCheckout || transferDepartureModeVisibleForCheckout;

    return (
        <React.Fragment>
            {bookingQuestionTree.map((node, index) => (
                <BookingQuestionNodeView
                    key={node.question.code || (node.question as { id?: string }).id}
                    node={node}
                    transferPickupBranch={null}
                    pickupTransferRulesApply={pickupTransferRulesApply}
                    transferArrivalModeVisibleForCheckout={transferArrivalModeVisibleForCheckout}
                    transferDepartureModeVisibleForCheckout={transferDepartureModeVisibleForCheckout}
                    detailLabel={detailLabel}
                    productQuestions={productQuestions}
                    questionData={questionData}
                    bookingAnswers={bookingAnswers}
                    dispatch={dispatch}
                    isLastInParentGroup={index === bookingQuestionTree.length - 1}
                />
            ))}
        </React.Fragment>
    );
};

const BookingQuestionNodeView: React.FC<{
    node: BookingQuestionTreeNode;
    transferPickupBranch: TransferPickupBranch;
    pickupTransferRulesApply: boolean;
    transferArrivalModeVisibleForCheckout: boolean;
    transferDepartureModeVisibleForCheckout: boolean;
    detailLabel: any;
    productQuestions: Question[] | undefined;
    questionData: any;
    bookingAnswers: any;
    dispatch: AppDispatch;
    depth?: number;
    isLastInParentGroup?: boolean;
}> = ({
    node,
    transferPickupBranch,
    pickupTransferRulesApply,
    transferArrivalModeVisibleForCheckout,
    transferDepartureModeVisibleForCheckout,
    detailLabel,
    productQuestions,
    questionData,
    bookingAnswers,
    dispatch,
    depth = 0,
    isLastInParentGroup = false,
}) => {
    const q = node.question as Question & { id?: string; label?: string };
    const questionId = q.code || q.id || '';
    const hasVisibleDependents = node.children.length > 0;
    const sectionWithDependentsStyle: React.CSSProperties | undefined = hasVisibleDependents
        ? {
              background: '#F5F5F5',
              padding: 15,
              borderRadius: 8,
              marginBottom: isLastInParentGroup ? 0 : 16,
          }
        : undefined;

    const dependentsWrapClass =
        node.children.length > 0 ? [depth > 0 ? 'ps-3' : null, 'mt-4'].filter(Boolean).join(' ') : '';

    const pickupTransferRulesForLocationQuestion =
        transferPickupBranch === 'departure'
            ? transferDepartureModeVisibleForCheckout
            : transferPickupBranch === 'arrival'
              ? transferArrivalModeVisibleForCheckout
              : pickupTransferRulesApply;

    const childTransferBranch = nextTransferPickupBranch(
        node.question as Question & { id?: string },
        transferPickupBranch
    );

    return (
        <div
            style={sectionWithDependentsStyle}
            className={!hasVisibleDependents ? (isLastInParentGroup ? 'mb-0' : 'mb-4') : undefined}
        >
            {q.units?.includes('LOCATION_REFERENCE') ? (
                <div className={hasVisibleDependents ? 'mb-0' : isLastInParentGroup ? 'mb-0' : 'mb-6'}>
                    <Text
                        className={q.required ? 'label required block mb-2' : 'label block mb-2'}
                        style={{ fontSize: '20px', color: '#262626', fontWeight: '600', display: 'block', textAlign: 'left' }}
                    >
                        {q.text || q.label}
                    </Text>
                    <PickupQuestion
                        key={`${questionId}-${transferPickupBranch ?? 'none'}`}
                        travelerPickups={detailLabel.travelerPickups || []}
                        allowCustomTravelerPickup={detailLabel.allowCustomTravelerPickup}
                        bookingAnswers={bookingAnswers}
                        bookingQuestions={detailLabel.questions}
                        transferPickupBranch={transferPickupBranch}
                        transferArrivalGatesPickup={pickupTransferRulesForLocationQuestion}
                        questionUnits={q.units}
                        valueUnit={bookingAnswers[questionId]?.unit}
                        value={bookingAnswers[questionId]?.value}
                        onChange={(val, unit) =>
                            dispatch(
                                setBookingAnswer({
                                    questionId: questionId,
                                    value: val,
                                    unit,
                                })
                            )
                        }
                    />
                </div>
            ) : (
                <div className="form-item text-start">
                    <Text className={q.required ? 'label required' : 'label'}>{q.text || q.label}</Text>

                    <Row gutter={8}>
                        <Col flex="auto">
                            <RenderDynamicInput
                                q={q}
                                productQuestions={productQuestions}
                                questionMetaList={questionData?.questions as QuestionMetaForOptions[] | undefined}
                                answer={bookingAnswers[questionId]}
                                onChange={(val: any) =>
                                    dispatch(
                                        setBookingAnswer({
                                            questionId: questionId,
                                            value: val,
                                            unit: bookingAnswers[questionId]?.unit,
                                        })
                                    )
                                }
                            />
                        </Col>

                        {q.units && q.units.length > 0 && (
                            <Col>
                                <Select
                                    value={bookingAnswers[questionId]?.unit || q.units[0]}
                                    onChange={(u: string) =>
                                        dispatch(
                                            setBookingAnswer({
                                                questionId: questionId,
                                                value: bookingAnswers[questionId]?.value,
                                                unit: u,
                                            })
                                        )
                                    }
                                    style={{ width: 120 }}
                                >
                                    {q.units.map((u: string) => (
                                        <Option key={u} value={u}>
                                            {u}
                                        </Option>
                                    ))}
                                </Select>
                            </Col>
                        )}
                    </Row>
                </div>
            )}

            {node.children.length > 0 && (
                <div className={dependentsWrapClass}>
                    {node.children.map((child, childIndex) => (
                        <BookingQuestionNodeView
                            key={child.question.code || (child.question as { id?: string }).id}
                            node={child}
                            transferPickupBranch={childTransferBranch}
                            pickupTransferRulesApply={pickupTransferRulesApply}
                            transferArrivalModeVisibleForCheckout={transferArrivalModeVisibleForCheckout}
                            transferDepartureModeVisibleForCheckout={transferDepartureModeVisibleForCheckout}
                            detailLabel={detailLabel}
                            productQuestions={productQuestions}
                            questionData={questionData}
                            bookingAnswers={bookingAnswers}
                            dispatch={dispatch}
                            depth={depth + 1}
                            isLastInParentGroup={childIndex === node.children.length - 1}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};
